import React from 'react';
import { pct, aud, delta, marginTone, cellBg } from './roleMarginMath';

const Delta = ({ v, suffix = ' pts', testId }) => {
  if (v == null || Math.abs(v) < 0.05) return null;
  return (
    <span className={`ml-1 text-[9px] font-semibold ${v > 0 ? 'text-emerald-700' : 'text-rose-600'}`} data-testid={testId}>
      {v > 0 ? '+' : '−'}{Math.abs(v).toFixed(1)}{suffix}
    </span>
  );
};

const Cell = ({ sim, base, min, testId, strong, extra = '' }) => {
  if (!sim) return <td className={`px-2 py-1.5 text-center text-slate-300 ${extra}`} data-testid={testId}>·</td>;
  const d = base ? delta(sim.margin_pct, base.margin_pct) : null;
  return (
    <td className={`px-2 py-1.5 text-right align-top ${cellBg(sim.margin_pct, min)} ${strong ? 'font-semibold' : ''} ${extra}`} data-testid={testId}
      title={`${sim.hours.toFixed(1)}h · sell ${aud(sim.sell_aud)} · cost ${aud(sim.cost_aud)} · margin ${aud(sim.margin_aud)}${sim.partial ? ' (includes roles without a cost rate)' : ''}`}>
      <div className={`tabular-nums ${marginTone(sim.margin_pct, min)}`}>{pct(sim.margin_pct)}<Delta v={d} /></div>
      <div className="text-[9px] text-slate-400 tabular-nums">{aud(sim.sell_aud)}{sim.cost_aud != null ? ` / ${aud(sim.cost_aud)}` : ' / no cost rate'}{sim.partial ? ' *' : ''}</div>
    </td>
  );
};

/** Groups (rows) × roles (columns) margin grid with role/group/overall totals. */
export function RoleMarginTable({ data, sim, base, min, compact }) {
  const find = (set, g, r) => set.cells.find((c) => c.group === g && c.role === r);
  return (
    <div className="rounded-lg border border-slate-200 overflow-x-auto">
      <table className={`w-full ${compact ? 'text-[11px]' : 'text-xs'}`} data-testid="role-margin-table">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">Component group</th>
            {data.roles.map((r) => (
              <th key={r} className="text-right px-2 py-2 font-semibold" title={data.role_totals[r]?.label} data-testid={`role-margin-head-${r}`}>{r}</th>
            ))}
            <th className="text-right px-3 py-2 font-semibold border-l border-slate-200">Group</th>
          </tr>
        </thead>
        <tbody>
          {data.groups.map((g) => (
            <tr key={g} className="border-t border-slate-100" data-testid={`role-margin-row-${g.replace(/[^\w]+/g, '-').toLowerCase()}`}>
              <td className="px-3 py-1.5 font-medium text-[#0F2040] whitespace-nowrap">{g}</td>
              {data.roles.map((r) => (
                <Cell key={r} sim={find(sim, g, r)} base={base ? find(base, g, r) : null} min={min} testId={`role-margin-cell-${g.replace(/[^\w]+/g, '-').toLowerCase()}-${r}`} />
              ))}
              <Cell sim={sim.group_totals[g]} base={base?.group_totals[g]} min={min} strong extra="border-l border-slate-200" testId={`role-margin-group-total-${g.replace(/[^\w]+/g, '-').toLowerCase()}`} />
            </tr>
          ))}
          <tr className="border-t-2 border-slate-300 bg-slate-50/70">
            <td className="px-3 py-1.5 font-semibold text-[#0F2040]">Role total</td>
            {data.roles.map((r) => (
              <Cell key={r} sim={sim.role_totals[r]} base={base?.role_totals[r]} min={min} strong testId={`role-margin-role-total-${r}`} />
            ))}
            <Cell sim={sim.overall} base={base?.overall} min={min} strong extra="border-l border-slate-200" testId="role-margin-overall" />
          </tr>
        </tbody>
      </table>
    </div>
  );
}
